import React, { createContext, useRef, useContext, useState } from 'react'
import { useDispatch } from 'react-redux'

import { toast } from '@/components/toasts/toast.helpers'
import { updateMediaPlayer } from '@/reducers/media/media.redux'

const AudioPlayerContext = createContext()

export const AudioPlayerProvider = ({ children }) => {
  const audioRef = useRef(null)
  const dispatch = useDispatch()
  const retryRef = useRef(0)
  const audioContextRef = useRef(null)
  const sourceRef = useRef(null)
  const monoGainRef = useRef(null)
  const [monoAudio, setMonoAudio] = useState(true)
  const [corsSupported, setCorsSupported] = useState(false)
  const retryTimeoutRef = useRef(null)

  const checkCorsSupport = (url) => {
    if (!url) {
      return
    }

    setCorsSupported(false)

    fetch(url, { method: 'HEAD', mode: 'cors' })
      .then(() => {
        setCorsSupported(true)

        if (audioRef.current && !audioRef.current.crossOrigin) {
          audioRef.current.crossOrigin = 'anonymous'
          audioRef.current.load()
        }
      })
      .catch(() => {
        // CORS not supported — audio will play in stereo without Web Audio API
      })
  }

  const changeAudioPlaybackRate = (speed) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speed
    }
  }

  const jumpAudioTo = (time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time
    }
  }

  const changeAudioVolume = (volume) => {
    if (audioRef.current) {
      audioRef.current.volume = volume
    }
  }

  const initAudioRouting = () => {
    if (audioContextRef.current || !audioRef.current) {
      return
    }

    let audioContext

    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const source = audioContext.createMediaElementSource(audioRef.current)

      sourceRef.current = source

      try {
        const monoGain = audioContext.createGain()

        monoGain.channelCount = 1
        monoGain.channelCountMode = 'explicit'
        monoGain.channelInterpretation = 'speakers'
        monoGain.connect(audioContext.destination)

        monoGainRef.current = monoGain
        source.connect(monoGain)
      } catch {
        // Mono setup failed but source is already captured from the audio element,
        // so connect directly to destination to preserve stereo playback
        source.connect(audioContext.destination)
      }

      audioContextRef.current = audioContext
    } catch {
      // AudioContext or createMediaElementSource failed,
      // audio element still plays through its default output
      if (audioContext) {
        audioContext.close()
      }
    }
  }

  const setMonoRouting = (enabled) => {
    if (!audioContextRef.current || !sourceRef.current || !monoGainRef.current) {
      return
    }

    try {
      sourceRef.current.disconnect()

      const target = enabled ? monoGainRef.current : audioContextRef.current.destination

      sourceRef.current.connect(target)
    } catch {
      // Routing swap failed — audio element's default output may still work
    }
  }

  const handleMonoAudioChange = () => {
    const newValue = !monoAudio

    setMonoAudio(newValue)
    setMonoRouting(newValue)
  }

  const tearDownAudioRouting = () => {
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
      sourceRef.current = null
      monoGainRef.current = null
    }

    if (audioRef.current && audioRef.current.crossOrigin) {
      audioRef.current.removeAttribute('crossOrigin')
      audioRef.current.load()
    }
  }

  const playAudio = () => {
    if (audioRef.current && audioRef.current.paused) {
      if (audioRef.current.crossOrigin) {
        initAudioRouting()
        setMonoRouting(monoAudio)
      }

      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {
          tearDownAudioRouting()
        })
      }

      audioRef.current.play()
    }
  }

  const pauseAudio = () => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause()
    }
  }

  const addAudioEvent = (eventType, callback) => {
    if (audioRef.current) {
      audioRef.current.addEventListener(eventType, callback)
    }
  }

  const removeAudioEvent = (eventType, callback) => {
    if (audioRef.current) {
      audioRef.current.removeEventListener(eventType, callback)
    }
  }

  const reloadAudio = () => {
    if (audioRef.current) {
      if (retryRef.current < 3) {
        retryRef.current += 1
        console.error(`Reloading audio... Attempt ${retryRef.current}`)

        // If CORS is causing the failure, fall back to non-CORS loading.
        // This sacrifices mono audio but prevents false "audio not available" errors.
        if (audioRef.current.crossOrigin) {
          tearDownAudioRouting()
          setCorsSupported(false)
        }

        clearTimeout(retryTimeoutRef.current)

        retryTimeoutRef.current = setTimeout(() => {
          if (!audioRef.current) {
            return
          }

          audioRef.current.load()

          if (audioRef.current.readyState >= 3) {
            console.info('Audio is already in a playable state')
            dispatch(updateMediaPlayer({ audioError: false }))
            retryRef.current = 0
          } else {
            audioRef.current.addEventListener(
              'canplaythrough',
              () => {
                console.info('Audio is ready to play')
                dispatch(updateMediaPlayer({ audioError: false }))
                retryRef.current = 0
                clearTimeout(retryTimeoutRef.current)
              },
              { once: true }
            )
          }
        }, retryRef.current * 1000)
      } else {
        console.error('Maximum reload attempts reached. Unable to reload audio.')
        toast.error('An error was encountered while loading the audio.')
        dispatch(updateMediaPlayer({ audioError: true, isPlaying: false }))
      }
    }
  }

  const handleAudioError = () => {
    if (audioRef.current) {
      console.error('Audio error encountered. Attempting to reload...')
      reloadAudio()
    }
  }

  React.useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener('error', handleAudioError)
    }

    return () => {
      clearTimeout(retryTimeoutRef.current)

      if (audioRef.current) {
        audioRef.current.removeEventListener('error', handleAudioError)
      }
      if (audioContextRef.current) {
        audioContextRef.current.close()
        audioContextRef.current = null
      }
    }
  }, [])

  return (
    <AudioPlayerContext.Provider
      value={{
        audioRef,
        playAudio,
        pauseAudio,
        jumpAudioTo,
        changeAudioPlaybackRate,
        changeAudioVolume,
        addAudioEvent,
        removeAudioEvent,
        monoAudio,
        handleMonoAudioChange,
        corsSupported,
        checkCorsSupport,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  )
}

export const useAudioPlayer = () => useContext(AudioPlayerContext) || {}
